import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { KOTA_ALL, PROVINSI } from "@/data/locations";
import { SourcesNote } from "@/components/SourcesNote";
import { currentHijriYear } from "@/lib/calendar/hijri";

const PULAU: Record<string, string[]> = {
  Sumatera: ["aceh", "sumatera-utara", "sumatera-barat", "riau", "kepulauan-riau", "jambi", "sumatera-selatan", "bangka-belitung", "bengkulu", "lampung"],
  Jawa: ["dki-jakarta", "jawa-barat", "banten", "jawa-tengah", "di-yogyakarta", "jawa-timur"],
  "Bali & Nusa Tenggara": ["bali", "nusa-tenggara-barat", "nusa-tenggara-timur"],
  Kalimantan: ["kalimantan-barat", "kalimantan-tengah", "kalimantan-selatan", "kalimantan-timur", "kalimantan-utara"],
  Sulawesi: ["sulawesi-utara", "gorontalo", "sulawesi-tengah", "sulawesi-barat", "sulawesi-selatan", "sulawesi-tenggara"],
  Maluku: ["maluku", "maluku-utara"],
  Papua: ["papua-barat", "papua-barat-daya", "papua", "papua-tengah", "papua-pegunungan", "papua-selatan"],
};

const POPULAR_KOTA = [
  "jakarta-pusat", "bandung", "surabaya", "medan", "makassar",
  "yogyakarta", "semarang", "palembang", "denpasar", "banjarmasin",
  "pontianak", "manado",
];

export const Route = createFileRoute("/jadwal-buka-puasa/")({
  head: () => {
    const title = "Jadwal Buka Puasa Hari Ini - Seluruh Indonesia | hari.co.id";
    const desc = "Jadwal buka puasa (waktu maghrib) untuk seluruh provinsi, kota, dan kecamatan di Indonesia. Akurat metode Kemenag RI, tersedia download PDF & preview.";
    const canonical = "https://hari.co.id/jadwal-buka-puasa/";
    const ld = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Jadwal Buka Puasa Indonesia",
      description: desc,
      url: canonical,
      about: { "@type": "Thing", name: "Iftar / Buka Puasa Ramadan" },
    };
    return {
      meta: [
        { title }, { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: canonical },
        { property: "og:image", content: "https://hari.co.id/og-jadwal-imsak.svg" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(ld) },
      ],
    };
  },
  component: BukaPuasaHub,
});

function BukaPuasaHub() {
  const popular = POPULAR_KOTA
    .map((s) => KOTA_ALL.find((k) => k.slug === s))
    .filter(Boolean) as typeof KOTA_ALL;
  const hy = currentHijriYear();

  return (
    <SiteShell>
      <div className="mx-auto max-w-5xl py-10">
        <nav className="mb-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">Beranda</Link> / Jadwal Buka Puasa
        </nav>
        <header className="rounded-xl border border-prayer/20 bg-prayer/5 p-6">
          <h1 className="font-display text-4xl tracking-tight md:text-5xl">
            Jadwal Buka Puasa Indonesia
          </h1>
          <p className="mt-3 max-w-3xl text-muted-foreground">
            Cek waktu berbuka puasa (maghrib) untuk seluruh kota & kecamatan di Indonesia.
            Akurat sesuai metode resmi Kementerian Agama RI — tersedia jadwal harian, bulanan,
            dan satu tahun penuh dengan preview & download PDF.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            <Link to="/jadwal-buka-puasa/lokasi/" className="rounded-md bg-prayer px-3 py-1.5 text-prayer-foreground hover:opacity-90">
              Indeks Lokasi Lengkap →
            </Link>
            <Link to="/jadwal-imsak/" className="rounded-md border border-border px-3 py-1.5 hover:bg-accent">
              Jadwal Imsakiyah
            </Link>
            <Link to="/jadwal-sholat/" className="rounded-md border border-border px-3 py-1.5 hover:bg-accent">
              Jadwal Sholat
            </Link>
            <Link to={`/kalender-hijriah/${hy}/9/` as never} className="rounded-md border border-border px-3 py-1.5 hover:bg-accent">
              Kalender Ramadhan {hy} H
            </Link>
          </div>
        </header>

        {/* Popular cities */}
        <section className="mt-8">
          <h2 className="text-xl font-semibold">Kota Besar</h2>
          <p className="mt-1 text-sm text-muted-foreground">Pilihan paling sering dicari untuk jadwal buka puasa.</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {popular.map((k) => (
              <Link
                key={k.slug}
                to={`/jadwal-buka-puasa/${k.slug}/` as never}
                className="rounded-md border border-border bg-card px-3 py-2 text-sm hover:border-prayer hover:bg-prayer/5"
              >
                Buka Puasa {k.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Per pulau */}
        <section className="mt-10">
          <h2 className="text-xl font-semibold">Jadwal Buka Puasa per Pulau</h2>
          <p className="mt-1 text-sm text-muted-foreground">Telusuri jadwal berbuka berdasarkan provinsi di tiap pulau utama Indonesia.</p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {Object.entries(PULAU).map(([pulau, slugs]) => {
              const provs = PROVINSI.filter((p) => slugs.includes(p.slug));
              return (
                <article key={pulau} className="rounded-lg border border-border bg-card p-4">
                  <h3 className="font-semibold">{pulau}</h3>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {provs.map((p) => (
                      <Link
                        key={p.slug}
                        to={`/jadwal-buka-puasa/${p.slug}/` as never}
                        className="rounded-md border border-border bg-background px-2.5 py-1 text-xs hover:border-prayer hover:text-prayer"
                      >
                        {p.name}
                      </Link>
                    ))}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* About / pSEO entity copy */}
        <section className="mt-10 grid gap-4 md:grid-cols-2">
          <article className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-semibold">Apa itu Buka Puasa?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              <strong>Buka puasa</strong> (Iftar) adalah momen mengakhiri puasa harian saat
              bulan <strong>Ramadan</strong> dan puasa sunnah lainnya, ditandai dengan masuknya
              waktu <strong>Maghrib</strong>. Sunnah Nabi menganjurkan berbuka dengan kurma dan
              air putih sebelum sholat Maghrib, lalu dilanjutkan makan secukupnya.
            </p>
          </article>
          <article className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-semibold">Kapan Ramadan {hy} H?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Bulan Ramadan adalah bulan ke-9 dalam <strong>kalender Hijriah</strong>.
              Lihat <Link to={`/kalender-hijriah/${hy}/9/` as never} className="text-prayer hover:underline">kalender Ramadhan {hy} H</Link>
              {" "}untuk daftar 30 hari penuh, atau cek <Link to={"/hari/idul-fitri/" as never} className="text-prayer hover:underline">Idul Fitri</Link> sebagai penanda akhir Ramadan.
            </p>
          </article>
        </section>

        {/* FAQ */}
        <section className="mt-10 rounded-lg border border-border bg-card p-5">
          <h2 className="text-xl font-semibold">FAQ Jadwal Buka Puasa</h2>
          <dl className="mt-3 space-y-3 text-sm">
            <div>
              <dt className="font-medium">Pukul berapa buka puasa hari ini?</dt>
              <dd className="mt-1 text-muted-foreground">Pilih kota Anda dari daftar di atas untuk melihat jam berbuka akurat sesuai zona waktu (WIB/WITA/WIT).</dd>
            </div>
            <div>
              <dt className="font-medium">Apakah jadwal sama untuk seluruh kecamatan dalam satu kota?</dt>
              <dd className="mt-1 text-muted-foreground">Selisih biasanya kurang dari 2 menit. Untuk akurasi optimal, pilih halaman kecamatan Anda.</dd>
            </div>
            <div>
              <dt className="font-medium">Bisakah saya download jadwal PDF?</dt>
              <dd className="mt-1 text-muted-foreground">Bisa. Buka halaman kota/kecamatan Anda lalu klik tombol PDF — pilih mode hari, sebulan, atau setahun, preview dulu sebelum download.</dd>
            </div>
          </dl>
        </section>

        <div className="mt-8">
          <SourcesNote variant="prayer" />
        </div>
      </div>
    </SiteShell>
  );
}
